/**
 * Authentication Utilities
 * JWT token management in localStorage
 */

import { jwtDecode } from 'jwt-decode';

const TOKEN_KEY = 'authToken';

/**
 * Save JWT token to localStorage
 * @param {string} token - JWT token from login/register response
 */
export const saveToken = (token) => {
  if (!token) {
    console.warn('[AUTH] Attempted to save empty token');
    return;
  }
  localStorage.setItem(TOKEN_KEY, token);
  console.log('[AUTH] Token saved to localStorage');
};

/**
 * Retrieve JWT token from localStorage
 * @returns {string|null} JWT token or null if not found
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Remove JWT token from localStorage (logout)
 */
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  console.log('[AUTH] Token cleared from localStorage');
};

/**
 * Check if JWT token is expired
 * @returns {boolean} true if token is expired or invalid
 */
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) return true;
  
  try {
    const decoded = jwtDecode(token);
    // Missing exp claim → treat as expired (security requirement)
    if (!decoded.exp) return true;
    const now = Date.now() / 1000; // JWT exp is in seconds
    return decoded.exp < now;
  } catch (err) {
    console.error('[AUTH] Invalid token:', err);
    return true; // Treat decode errors as expired
  }
};

/**
 * Get decoded JWT token payload
 * @returns {object|null} Decoded token payload (userId, username, exp, etc.) or null if invalid
 */
export const getTokenPayload = () => {
  const token = getToken();
  if (!token) return null;
  
  try {
    return jwtDecode(token);
  } catch (err) {
    console.error('[AUTH] Failed to decode token:', err);
    return null;
  }
};

/**
 * Check if user is authenticated (has valid, non-expired token)
 * @returns {boolean} true if token exists and is not expired
 */
export const isAuthenticated = () => {
  return !!getToken() && !isTokenExpired();
};
